import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Input, notification } from "antd";
import {
  ArrowRightOutlined,
  CloseOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  CommentOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
  ExpandAltOutlined,
  FilePdfOutlined,
  FolderOutlined,
  PlusOutlined,
  SendOutlined,
  ShrinkOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  askHelpChat,
  downloadHelpChatPdf,
  uploadHelpChatFiles,
} from "../../services/aiAPI";
import { useAuth } from "../../hooks/useAuth";
import SparkleIcon from "./SparkleIcon";
import {
  HELP_CHAT_CREATE_QUERY_KEYS,
  HELP_CHAT_MAX_FILE_MB,
  HELP_CHAT_MAX_FILES,
  HELP_CHAT_OPEN_EVENT,
  HELP_CHAT_SUGGESTIONS,
  canOpenHelpChatRoute,
  formatHelpChatText,
  helpChatHasTable,
  helpChatInternalPath,
  helpChatModuleLabel,
  helpChatRouteLabel,
  helpChatSourceLabel,
} from "../../utils/helpChatUtils";

const TypingIndicator = () => (
  <div className="mb-3 flex justify-start">
    <div className="flex min-h-9 items-center gap-2 rounded-xl rounded-bl-sm border border-gray-200 bg-gray-100 px-3 py-2.5 text-gray-900">
      <span className="text-xs text-gray-500">Help is typing</span>
      <span className="inline-flex items-center gap-1" aria-hidden="true">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
      </span>
    </div>
  </div>
);

const ContextTag = ({ icon, label, kind, attached, onToggle }) =>
  attached ? (
    <span
      className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md bg-white/10 py-1 pl-2 pr-1 text-xs text-white/85"
      title={`Answers use this ${kind}. Remove it to ask a general question.`}
    >
      <span className="shrink-0 text-white/60">{icon}</span>
      <span className="truncate">{label}</span>
      <button
        type="button"
        className="inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0 text-[10px] text-white/60 hover:bg-white/15 hover:text-white"
        onClick={onToggle}
        aria-label={`Remove ${kind} from question`}
      >
        <CloseOutlined />
      </button>
    </span>
  ) : (
    <button
      type="button"
      className="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-white/25 bg-transparent px-2 py-1 text-xs text-white/55 hover:border-white/50 hover:text-white/85"
      onClick={onToggle}
      aria-label={`Add ${kind} to question`}
    >
      <PlusOutlined className="shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );

/**
 * Floating help chatbot: product guidance from freemium_help docs (context pack).
 * Styled with Tailwind to match the dark left sidebar (#001529).
 */
const HelpChatWidget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useAuth();
  const queryClient = useQueryClient();
  const userRole = selectedProject?.role;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pdfLoadingIndex, setPdfLoadingIndex] = useState(null);
  const [chatFiles, setChatFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const dragDepthRef = useRef(0);
  const [projectAttached, setProjectAttached] = useState(true);
  const [moduleAttached, setModuleAttached] = useState(true);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const currentProjectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name || "Selected project";
  const moduleLabel = helpChatModuleLabel(location.pathname);
  const projectFiles = chatFiles.filter(
    (f) => f.projectId === currentProjectId
  );
  const pendingFiles = projectFiles.filter((f) => !f.sent);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener(HELP_CHAT_OPEN_EVENT, openChat);
    return () => window.removeEventListener(HELP_CHAT_OPEN_EVENT, openChat);
  }, []);

  useEffect(() => {
    setProjectAttached(true);
  }, [currentProjectId]);

  useEffect(() => {
    setModuleAttached(true);
  }, [moduleLabel]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open]);

  const close = () => {
    abortRef.current?.abort();
    setLoading(false);
    setOpen(false);
  };

  const openRoute = (route) => {
    navigate(route);
    close();
  };

  const handleAnswerLink = (event, href) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    const path = helpChatInternalPath(href);
    if (!path) return;
    event.preventDefault();
    openRoute(path);
  };

  const downloadPdf = async (message, index) => {
    if (pdfLoadingIndex !== null) return;
    setPdfLoadingIndex(index);
    try {
      const pdf = await downloadHelpChatPdf(message.pdfTitle, message.content);
      const url = URL.createObjectURL(pdf);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${message.pdfTitle.replace(/[^a-zA-Z0-9_-]+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      let description = "Please try again.";
      try {
        const body = error.response?.data;
        const data = body instanceof Blob ? JSON.parse(await body.text()) : body;
        const detail = data?.detail;
        description = (typeof detail === "string" ? detail : detail?.message) || description;
      } catch {}
      notification.error({ message: "Could not create the PDF", description });
    } finally {
      setPdfLoadingIndex(null);
    }
  };

  const canAttachFiles = !loading && !uploading && !!currentProjectId;

  const pickChatFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    uploadChatFiles(picked);
  };

  const hasDraggedFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDraggingFiles(true);
  };

  const handleDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = canAttachFiles ? "copy" : "none";
  };

  const handleDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDraggingFiles(false);
  };

  const handleDrop = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDraggingFiles(false);
    if (!canAttachFiles) return;
    const items = Array.from(event.dataTransfer.items || []);
    const dropped = Array.from(event.dataTransfer.files || []).filter(
      (file, index) => !items[index]?.webkitGetAsEntry?.()?.isDirectory
    );
    if (dropped.length < event.dataTransfer.files.length) {
      notification.warning({ message: "Folders can't be attached. Drop files instead." });
    }
    uploadChatFiles(dropped);
  };

  const uploadChatFiles = async (picked) => {
    if (!picked.length || !currentProjectId) return;
    if (projectFiles.length + picked.length > HELP_CHAT_MAX_FILES) {
      notification.warning({
        message: `Help can use up to ${HELP_CHAT_MAX_FILES} files in one chat.`,
      });
      return;
    }
    const totalBytes = [...projectFiles, ...picked].reduce(
      (sum, file) => sum + (file.size || 0),
      0
    );
    if (totalBytes > HELP_CHAT_MAX_FILE_MB * 1024 * 1024) {
      notification.warning({
        message: `Help accepts up to ${HELP_CHAT_MAX_FILE_MB} MB of files in total in one chat.`,
      });
      return;
    }
    const projectId = currentProjectId;
    const formData = new FormData();
    picked.forEach((file) => formData.append("files", file));
    setUploading(true);
    try {
      const data = await uploadHelpChatFiles(projectId, formData);
      const uploaded = (data.files || [])
        .map((f, index) => ({ ...f, size: picked[index]?.size || 0 }))
        .filter((f) => f.result !== false);
      if (uploaded.length < picked.length) {
        notification.error({ message: "Some files could not be uploaded." });
      }
      setChatFiles((prev) => [
        ...prev,
        ...uploaded.map((f) => ({
          id: f._id || f.id,
          filename: f.filename,
          size: f.size,
          projectId,
          sent: false,
        })),
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const description = typeof detail === "string" ? detail : detail?.message;
      notification.error({
        message: "Upload failed",
        description: description || "Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeChatFile = (id) => {
    setChatFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || loading || uploading) return;

    // Cap each turn: API HelpChatTurn.content max_length=4000. Without this,
    // a long assistant reply 422s every later send until page reload.
    const projectId = currentProjectId;
    const history = messages.slice(-16).map((m) => ({
      role: m.role,
      content: (m.role === "user" && m.context
        ? `[Context: Project: ${m.context.project}${
            m.context.module ? ` · Module: ${m.context.module}` : ""
          }]\n${m.content}`
        : String(m.content || "")
      ).slice(0, 4000),
    }));

    const fileIds = projectFiles.map((f) => f.id);
    const sentNames = pendingFiles.map((f) => f.filename);
    setInput("");
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: text,
        fileNames: sentNames,
        context: {
          project: projectAttached ? projectName : "All projects",
          module: moduleLabel && moduleAttached ? moduleLabel : null,
        },
      },
    ]);
    setChatFiles((prev) =>
      prev.map((f) => (f.projectId === projectId ? { ...f, sent: true } : f))
    );
    setLoading(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const data = await askHelpChat(text, {
        history,
        signal: controller.signal,
        projectId: projectAttached ? projectId : undefined,
        page: moduleLabel && !moduleAttached ? undefined : location.pathname,
        fileIds,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.answer || "No answer returned.",
          routes: Array.isArray(data.related_routes) ? data.related_routes : [],
          toolsUsed: Array.isArray(data.tools_used) ? data.tools_used : [],
          pdfTitle: data.pdf_title || null,
        },
      ]);
      if (helpChatHasTable(data.answer)) setExpanded(true);
      if (Array.isArray(data.file_ids)) {
        const liveIds = data.file_ids;
        setChatFiles((prev) =>
          prev.filter(
            (f) =>
              f.projectId !== projectId ||
              !fileIds.includes(f.id) ||
              liveIds.includes(f.id)
          )
        );
      }
      const toolsUsed = Array.isArray(data.tools_used) ? data.tools_used : [];
      toolsUsed.forEach((tool) => {
        (HELP_CHAT_CREATE_QUERY_KEYS[tool] || []).forEach((key) => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      });
    } catch (err) {
      if (
        axios.isCancel?.(err) ||
        err?.code === "ERR_CANCELED" ||
        err?.name === "CanceledError"
      ) {
        return;
      }
      const detail = err?.response?.data?.detail;
      const detailText =
        (typeof detail === "string" ? detail : detail?.message) || "";
      const status = err?.response?.status;
      const showInChat = status >= 400 && status < 500 && detailText;
      if (!showInChat) {
        notification.error({
          message: "Help chat unavailable",
          description: detailText || "Please try again later.",
        });
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: showInChat
            ? detailText
            : "Sorry — help chat is temporarily unavailable. Please try again.",
          routes: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className="help-chat-launcher fixed bottom-6 right-6 z-[99] inline-flex h-14 cursor-pointer items-center gap-2.5 rounded-full px-6 text-base font-semibold text-white shadow-[0_6px_16px_rgba(0,0,0,0.25)] outline-none transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(22,119,255,0.4)] focus-visible:ring-2 focus-visible:ring-[#1677ff] focus-visible:ring-offset-2"
          onClick={() => setOpen(true)}
          aria-label="Ask AI"
        >
          <SparkleIcon size={23} />
          <span>Ask AI</span>
        </button>
      )}

      {open && (
        <div
          className={`fixed bottom-6 right-6 z-[1050] flex flex-col overflow-hidden rounded-xl border border-white/10 bg-[#001529] shadow-[0_12px_40px_rgba(0,0,0,0.45)] ${
            expanded
              ? "h-[min(720px,calc(100vh-48px))] w-[min(760px,calc(100vw-32px))]"
              : "h-[min(560px,calc(100vh-48px))] w-[min(400px,calc(100vw-32px))]"
          }`}
          role="dialog"
          aria-label="Help chat"
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-[#001529] px-4 py-3.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <CommentOutlined className="text-lg text-white/85" />
              <div>
                <div className="text-[15px] font-semibold leading-tight text-white">
                  Help
                </div>
                <div className="text-xs leading-snug text-white/45">
                  SubmittalLink assistant
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-white hover:bg-white/10"
                onClick={() => setExpanded((value) => !value)}
                aria-label={expanded ? "Shrink help" : "Expand help"}
              >
                {expanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
              </button>
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-white hover:bg-white/10"
                onClick={close}
                aria-label="Close help"
              >
                <CloseOutlined />
              </button>
            </div>
          </header>

          <div
            className="relative flex min-h-0 flex-1 flex-col"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {draggingFiles && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[#001529]/85 p-6">
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#1677ff] text-center text-white">
                  <PaperClipOutlined className="text-3xl" />
                  <div className="text-sm font-semibold">
                    {canAttachFiles ? "Drop files to attach" : "Files can't be attached now"}
                  </div>
                  <div className="text-xs text-white/55">
                    {currentProjectId
                      ? `Up to ${HELP_CHAT_MAX_FILES} files, ${HELP_CHAT_MAX_FILE_MB} MB in total`
                      : "Select a project to attach files"}
                  </div>
                </div>
              </div>
            )}

            <div
              ref={listRef}
              className="min-h-0 flex-1 overflow-auto bg-white p-4 text-gray-900"
            >
              {messages.length === 0 && !loading && (
                <div>
                  <p className="mb-3 text-[13px] leading-relaxed text-gray-500">
                    Ask how to use SubmittalLink, or ask about your project's
                    submittals, RFIs and daily reports.
                  </p>
                  <div className="flex flex-col gap-2">
                    {HELP_CHAT_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="cursor-pointer rounded-lg border border-[#001529]/10 bg-[#f5f7fa] px-3 py-2.5 text-left text-[13px] text-[#001529] transition-colors hover:border-[#1677ff] hover:bg-[#1677ff] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => send(s)}
                        disabled={loading}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => {
                const isUser = m.role === "user";
                const visibleRoutes = isUser
                  ? []
                  : (m.routes || []).filter((route) =>
                      canOpenHelpChatRoute(route, userRole)
                    );
                const sourceLabel = isUser
                  ? ""
                  : helpChatSourceLabel(m.toolsUsed);
                const hasTable = !isUser && helpChatHasTable(m.content);
                return (
                  <div
                    key={`${m.role}-${idx}`}
                    className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`flex flex-col gap-2 ${
                        hasTable ? "w-full" : "max-w-[92%]"
                      } ${isUser ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`max-w-full rounded-xl px-3 py-2.5 text-[13px] leading-relaxed ${
                          hasTable ? "w-full" : ""
                        } ${
                          isUser
                            ? "rounded-br-sm bg-[#1677ff] text-white"
                            : "rounded-bl-sm border border-gray-200 bg-gray-100 text-gray-900"
                        }`}
                      >
                        <div
                          className={`mb-1 text-[11px] font-semibold ${
                            isUser ? "text-white/70" : "text-gray-500"
                          }`}
                        >
                          {isUser ? "You" : "Help"}
                        </div>
                        {isUser ? (
                          <div className="whitespace-pre-wrap break-words">
                            {m.content}
                            {(m.fileNames || []).length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {m.fileNames.map((name) => (
                                  <span
                                    key={name}
                                    className="inline-flex items-center gap-1 rounded bg-white/20 px-1.5 py-0.5 text-[11px]"
                                  >
                                    <PaperClipOutlined />
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="break-words">
                            {formatHelpChatText(m.content, handleAnswerLink)}
                          </div>
                        )}
                        {sourceLabel && (
                          <div className="mt-2 flex items-center gap-1.5 border-t border-gray-200 pt-1.5 text-[11px] text-gray-500">
                            <DatabaseOutlined />
                            <span>{sourceLabel}</span>
                          </div>
                        )}
                      </div>

                      {!isUser && m.pdfTitle && (
                        <Button
                          size="small"
                          icon={<FilePdfOutlined />}
                          loading={pdfLoadingIndex === idx}
                          disabled={pdfLoadingIndex !== null && pdfLoadingIndex !== idx}
                          onClick={() => downloadPdf(m, idx)}
                        >
                          Download PDF
                        </Button>
                      )}

                      {isUser && m.context && (
                        <div className="flex max-w-full flex-wrap justify-end gap-1">
                          {[
                            { icon: <FolderOutlined />, label: m.context.project },
                            { icon: <AppstoreOutlined />, label: m.context.module },
                          ]
                            .filter((chip) => chip.label)
                            .map((chip, chipIdx) => (
                              <span
                                key={chipIdx}
                                className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] leading-tight text-gray-500"
                              >
                                <span className="shrink-0">{chip.icon}</span>
                                <span className="truncate">{chip.label}</span>
                              </span>
                            ))}
                        </div>
                      )}

                      {visibleRoutes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pl-0.5">
                          {visibleRoutes.map((route) => (
                            <button
                              key={route}
                              type="button"
                              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-[#001529] px-3 py-1.5 text-xs font-medium leading-tight text-white transition-colors hover:bg-[#1677ff] active:scale-[0.98]"
                              onClick={() => openRoute(route)}
                            >
                              <span>Open {helpChatRouteLabel(route)}</span>
                              <ArrowRightOutlined className="text-[10px] opacity-85" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {loading && <TypingIndicator />}
            </div>

            <footer className="shrink-0 border-t border-white/10 bg-[#001529] px-4 pb-3 pt-3">
              {(currentProjectId || moduleLabel) && (
                <div className="mb-2 flex min-w-0 flex-wrap gap-1.5">
                  {currentProjectId && (
                    <ContextTag
                      icon={<FolderOutlined />}
                      label={projectName}
                      kind="project"
                      attached={projectAttached}
                      onToggle={() => setProjectAttached((value) => !value)}
                    />
                  )}
                  {moduleLabel && (
                    <ContextTag
                      icon={<AppstoreOutlined />}
                      label={moduleLabel}
                      kind="module"
                      attached={moduleAttached}
                      onToggle={() => setModuleAttached((value) => !value)}
                    />
                  )}
                </div>
              )}
              {(pendingFiles.length > 0 || uploading) && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {pendingFiles.map((f) => (
                    <span
                      key={f.id}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-white/10 px-2 py-1 text-xs text-white/85"
                    >
                      <PaperClipOutlined />
                      <span className="max-w-[220px] truncate">{f.filename}</span>
                      <button
                        type="button"
                        className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-white/60 hover:text-white"
                        onClick={() => removeChatFile(f.id)}
                        aria-label={`Remove ${f.filename}`}
                      >
                        <CloseOutlined className="text-[10px]" />
                      </button>
                    </span>
                  ))}
                  {uploading && (
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-2 py-1 text-xs text-white/70">
                      <LoadingOutlined /> Uploading…
                    </span>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={pickChatFiles}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-white/80 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canAttachFiles}
                  aria-label="Attach files"
                >
                  <PaperClipOutlined />
                </button>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question…"
                  disabled={loading}
                  onPressEnter={() => send()}
                  maxLength={2000}
                  className="!flex-1 !rounded-lg !border-white/10 !bg-[#0b213a] !text-white/90 placeholder:!text-white/35 hover:!border-[#1677ff] focus:!border-[#1677ff] focus:!shadow-none"
                />
                <Button
                  type="primary"
                  className="!inline-flex !h-8 !w-9 !shrink-0 !items-center !justify-center !border-[#1677ff] !bg-[#1677ff] !p-0 !text-white hover:!border-[#4096ff] hover:!bg-[#4096ff] disabled:!border-transparent disabled:!bg-[#1677ff]/35 disabled:!text-white/85 disabled:!opacity-100 [&_.anticon]:!text-white disabled:[&_.anticon]:!text-white/85"
                  icon={<SendOutlined />}
                  onClick={() => send()}
                  disabled={!input.trim() || loading || uploading}
                  aria-label="Send"
                />
              </div>
              <p className="mb-0 mt-2 text-center text-xs leading-tight text-white/40">
                AI can make mistakes. Check important info.
              </p>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpChatWidget;
